import { Button, Stack } from '@mui/material';
import React from 'react';
import {
  LAST_6_HOURS,
  TIME_FRAME_TYPES,
  TIME_RANGE_TYPES,
} from 'stoerk-ui-components';
import moment from 'moment';
import RefreshIcon from '@mui/icons-material/Refresh';
import DateTimeRangePicker from '../../../../../commons/DateTimeRangePicker/DateTimeRangePicker';
import LabButtonCombo from '../Lab/LabButtonCombo';
import { ChartsFiltersProps } from './ChartsFilters';
import { useDevicesControlUnits } from '../../../../../../redux/controlUnit/hooks/useDevicesControlUnits';
import { useControlUnitsInComposableDevice } from '../../../../../../redux/monitoring/hooks/useControlUnitsInComposableDevice';
import DigitalChannelFiltersComposable from './DigitalChannelFiltersComposable';

export interface ChartsFiltersComposableProps
  extends Omit<ChartsFiltersProps, 'deviceId' | 'controlUnitId'> {
  groupId: string;
  polyglot: any;
}

/**
 * Enhanced ChartsFilters component that uses composable device labels
 * instead of raw device channel names in filter components
 */
export const ChartsFiltersComposable = (
  props: ChartsFiltersComposableProps
) => {
  const [devicesControlUnits] = useDevicesControlUnits();
  const enhancedDevicesControlUnits = useControlUnitsInComposableDevice(
    props.groupId
  );
  const {
    analogControls: [analogFilterValue, setAnalogFilterValue],
    digitalControls: [digitalFilterValue, setDigitalFilterValue],
    labStateFilters,
    timeFrame,
    polyglot,
    groupId,
  } = props;

  const handleRefresh = () => {
    const newEnd = moment(); // Max = Current date and time
    const interval = newEnd.diff(timeFrame.end, 'milliseconds');
    const newStart = timeFrame.start.add(interval, 'milliseconds'); //Min = oldMin + interval

    props.onChangeDatePicker({
      start: newStart,
      end: newEnd,
      timeFrameType: TIME_FRAME_TYPES.CUSTOM,
      timeRangeType: TIME_RANGE_TYPES.ABSOLUTE,
    });
  };

  return (
    <Stack
      spacing={2}
      direction={{ xs: 'column', sm: 'row' }}
      sx={{ margin: 2 }}
    >
      {/* Enhanced analog channel filters with composable device names */}
      <DigitalChannelFiltersComposable
        devicesControlUnits={enhancedDevicesControlUnits}
        type="channels"
        value={analogFilterValue}
        onSave={setAnalogFilterValue}
        composableDeviceId={groupId}
      />

      {/* Enhanced digital channel filters with composable device names */}
      <DigitalChannelFiltersComposable
        devicesControlUnits={enhancedDevicesControlUnits}
        type="events"
        value={digitalFilterValue}
        onSave={setDigitalFilterValue}
        composableDeviceId={groupId}
      />

      {/* Standard date/time picker */}
      <DateTimeRangePicker
        selectedTimeframe={LAST_6_HOURS}
        onChange={props.onChangeDatePicker}
      />

      {/* Standard refresh button */}
      <Button
        onClick={handleRefresh}
        variant="outlined"
        startIcon={<RefreshIcon />}
        disabled={timeFrame.timeRangeType === TIME_RANGE_TYPES.ABSOLUTE}
      >
        {polyglot.t('button.refresh')}
      </Button>

      {/* Standard lab controls */}
      <LabButtonCombo
        labStateFilters={labStateFilters}
        devicesControlUnits={devicesControlUnits}
      />
    </Stack>
  );
};

export default ChartsFiltersComposable;
